<template>
    <div>
        <limitless-page-header
            title="Leverantörer"
            sub-title="Produktreklamationer"
        >
            <template #header-elements>
                <limitless-page-header-button :route="{ name: 'complaints.edit', params: { id: 0 } }" icon="icon-plus3">Skapa ny</limitless-page-header-button>
            </template>
        </limitless-page-header>

        <div class="content">
            <rednax-datatable
                ref="complaintstable"
                :url="'/vue/suppliers/complaints'"
                :columns="table_columns"
                :filter="table_filter"
                sort-by="id"
                sort-order="desc"
                @updateFilter="updateFilter"
                @rowclick="rowClick"
                history
                :per-page="100"
            >
                <div class="row">
                    <div class="col-lg-3">
                        <div class="form-group">
                            <input type="text" class="form-control" placeholder="Sök..." v-model="table_filter.search">
                        </div>
                    </div>
                    <div class="col-lg-3">
                        <v-date-picker
                            v-model="date"
                            mode="date"
                            :locale="{ id: 'sv', firstDayOfWeek: 2, masks: { weekdays: 'WW', input: 'YYYY-MM-DD' } }"
                            :model-config="{ type: 'string', mask: 'YYYY-MM-DD' }"
                            is-range
                            show-iso-weeknumbers
                        >
                            <template v-slot="{ inputValue, inputEvents }">
                                <div class="row">
                                    <b-col style="position:relative;">
                                        <div class="input-group">
                                            <span class="input-group-prepend">
                                                <span class="input-group-text">
                                                    <i class="icon-calendar2"></i>
                                                </span>
                                            </span>
                                            <input
                                                class="form-control"
                                                :value="(inputValue.start && inputValue.end ? inputValue.start + ' - ' + inputValue.end : '')"
                                                v-on="inputEvents.start"
                                                placeholder="Datumintervall..."
                                            />
                                            <span class="input-group-append">
                                                <button type="button" class="btn btn-light px-2" @click="date = null">
                                                    <i class="icon-cross"></i>
                                                </button>
                                            </span>
                                        </div>
                                    </b-col>
                                </div>
                            </template>
                        </v-date-picker>
                    </div>
                    <div class="col-3">
                        <supplier-filter
                            v-model="table_filter.supplier_id"
                            :reduce="(opt) => opt.id"
                            placeholder="-- Alla leverantörer --"
                        ></supplier-filter>
                    </div>
                    <div class="col-3">
                        <vue-select
                            v-model="table_filter.status"
                            :options="statuses"
                            :reduce="(opt) => opt.id"
                            placeholder="-- Alla statusar --"
                            :selectable="(opt) => !opt.disabled"
                        ></vue-select>
                    </div>
                </div>

                <div class="card-body border-top d-flex justify-content-between" style="margin: 20px -20px -20px -20px;">
                    <div>
                        <b-button variant="light" @click="selectAllThisPage">Markera/Avmarkera alla</b-button>
                    </div>
                    <div>
                        <limitless-button
                            btn-class="btn-secondary ml-2"
                            @click="showModalSend"
                            icon="icon-paperplane"
                            :busy="busy_sending"
                            :disabled="checked.length === 0 || busy_completing || !selected_is_same_supplier"
                        >Batch skicka</limitless-button>
                        <limitless-button
                            ref="btncompletecomplaint"
                            btn-class="btn-success ml-2"
                            icon="icon-checkmark4"
                            @click="setCompleted"
                            :busy="busy_completing"
                            :disabled="checked.length === 0 || busy_sending"
                        >Batch avsluta</limitless-button>
                    </div>
                </div>

                <template #cell_id="{ row }">
                    <router-link :to="{ name: 'complaints.edit', params: { id: row.id }}">{{ row.id }}</router-link>
                </template>

                <template #cell_status="{ row }">
                    <div v-if="row.completed_at" class="badge badge-secondary">Avslutad</div>
                    <div v-else-if="row.sent_at" class="badge badge-warning">Skickad</div>
                    <div v-else class="badge badge-yellow">Ny</div>
                </template>

                <template #cell_ProdName="{ row }">
                    {{ row.prod_id }} - {{ row.ProdName }}{{ row.ProdSupplierSKU ? ' (' + row.ProdSupplierSKU + ')' : '' }}
                </template>

                <template #cell_pdf_url="{ row }">
                    <a v-if="row.pdf_url" :href="row.pdf_url" target="_blank">Visa</a>
                </template>

                <template #cell_checkbox="{ row }">
                    <div class="custom-control custom-checkbox">
                        <input type="checkbox" class="custom-control-input" :id="'chk-complaint-' + row.id" v-model="checked" :value="row">
                        <label class="custom-control-label pl-0" :for="'chk-complaint-' + row.id"></label>
                    </div>
                </template>
            </rednax-datatable>
        </div>

        <b-modal id="modal-send" title="Skicka" size="lg" @ok="sendComplaint">
            <ValidationObserver ref="sendcomplaintform">
                <div class="form-group row">
                    <label class="col-form-label col-lg-2">Skickas från</label>
                    <div class="col-lg-10">
                        <input-with-validation
                            label="Från"
                            v-model="send.from"
                            :disabled="busy_sending"
                            rules="required"
                            hide-label
                        />
                    </div>
                </div>
                <div class="form-group row">
                    <label class="col-form-label col-lg-2">Skickas till</label>
                    <div class="col-lg-10">
                        <input-with-validation
                            label="Till"
                            v-model="send.to"
                            :disabled="busy_sending"
                            rules="required"
                            hide-label
                        />
                    </div>
                </div>
                <div class="form-group row">
                    <label class="col-form-label col-lg-2">Ärende</label>
                    <div class="col-lg-10">
                        <input-with-validation
                            label="Ärende"
                            v-model="send.subject"
                            :disabled="busy_sending"
                            rules="required"
                            hide-label
                        />
                    </div>
                </div>
                <div class="form-group row">
                    <label class="col-form-label col-lg-2">Bifoga</label>
                    <div class="col-lg-10 pt-2">
                        <div class="custom-control custom-checkbox custom-control-inline">
                            <input type="checkbox" class="custom-control-input" id="chk-attach-pdf" v-model="send.attach_pdf">
                            <label class="custom-control-label" for="chk-attach-pdf">PDF</label>
                        </div>
                        <div class="custom-control custom-checkbox custom-control-inline">
                            <input type="checkbox" class="custom-control-input" id="chk-attach-pictures" v-model="send.attach_pictures">
                            <label class="custom-control-label" for="chk-attach-pictures">Bilder</label>
                        </div>
                    </div>
                </div>
                <div class="form-group row">
                    <label class="col-form-label col-lg-2">Meddelande</label>
                    <div class="col-lg-10">
                        <ValidationProvider
                            vid="message"
                            name="Meddelande"
                            rules="required"
                            v-slot="{ errors }"
                        >
                            <elastic-textarea style="min-height: 170px;" class="form-control" placeholder="Meddelande" v-model="send.message" :class="{'border-danger': errors[0] }"></elastic-textarea>
                            <span class="validation-invalid-label">{{ errors[0] }}</span>
                        </ValidationProvider>
                    </div>
                </div>
            </ValidationObserver>
            <template #modal-footer="{ cancel, ok }">
                <b-button variant="light" @click="cancel">Avbryt</b-button>
                <limitless-button ref="btnsendpurchase" type="button" @click="ok" icon="icon-paperplane" :busy="busy_sending">Skicka</limitless-button>
            </template>
        </b-modal>
    </div>
</template>

<script>
import LimitlessPageHeader from '@components/Layout/partials/LimitlessPageHeader';
import RednaxDatatable from '@components/Datatable/RednaxDatatableComponent';
import SupplierFilter from '@components/Filters/SupplierFilter';
import VueSelect from '@components/Wrappers/VueSelect';
import VDatePicker from 'v-calendar/lib/components/date-picker.umd';
import LimitlessButton from '@components/Limitless/LimitlessButton';
import apiClient from '@services/ApiClient';
import { mapState } from 'pinia';
import ElasticTextarea from '@components/ElasticTextarea';
import InputWithValidation from '@components/Form/InputWithValidation';
import LimitlessPageHeaderButton from '@components/Layout/partials/LimitlessPageHeaderButton';
import { useAuthStore } from '@services/pinia/AuthStore';

export default {
    components: {
        LimitlessPageHeaderButton,
        InputWithValidation,
        ElasticTextarea,
        LimitlessButton,
        VDatePicker,
        SupplierFilter,
        RednaxDatatable,
        LimitlessPageHeader,
        VueSelect,
    },
    watch: {
        date: {
            deep: true,
            handler(date) {
                if (date && date.start && date.end) {
                    this.table_filter.date_start = date.start;
                    this.table_filter.date_end = date.end;
                } else {
                    this.table_filter.date_start = '';
                    this.table_filter.date_end = '';
                }
            },
        },
    },
    data: () => ({
        busy_completing: false,
        busy_sending: false,
        date: null,
        table_filter: {
            search: '',
            supplier_id: '',
            status: 'active',
            date_start: '',
            date_end: '',
        },
        table_columns: [
            {
                header: '',
                data: 'checkbox',
                sortable: false,
                clickable: false,
            },
            {
                header: '#',
                data: 'id',
                sortable: true,
            },
            {
                header: 'Status',
                data: 'status',
                sortable: false,
            },
            {
                header: 'Leverantör',
                data: 'SupplierName',
                sortable: true,
            },
            {
                header: 'Produkt',
                data: 'ProdName',
                sortable: true,
            },
            {
                header: 'Skapad',
                data: 'created_at',
                sortable: true,
                render(row) {
                    return row.created_at.substring(0, 10);
                },
            },
            {
                header: 'Skickad',
                data: 'sent_at',
                sortable: true,
                render(row) {
                    return row.sent_at ? row.sent_at.substring(0, 10) : '';
                },
            },
            {
                header: 'Avslutad',
                data: 'completed_at',
                sortable: true,
                render(row) {
                    return row.completed_at ? row.completed_at.substring(0, 10) : '';
                },
            },
            {
                header: 'PDF',
                data: 'pdf_url',
                sortable: false,
            },
        ],
        statuses: [
            { id: 'active', label: 'Pågående' },
            { id: '---', label: '----------------------', disabled: true },
            { id: 'new', label: 'Ny' },
            { id: 'sent', label: 'Skickad' },
            { id: 'closed', label: 'Avslutad' },
        ],
        checked: [],
        send: {
            from: '',
            to: '',
            attach_pdf: true,
            attach_pictures: false,
            subject: '',
            message: '',
        },
        selected_complaint_ids: [],
    }),
    methods: {
        rowClick(row) {
            this.$router.push({ name: 'complaints.edit', params: { id: row.id } });
        },
        updateFilter(filter) {
            this.table_filter = filter;
            if (filter.date_start && filter.date_end) {
                this.date = {
                    start: filter.date_start,
                    end: filter.date_end,
                };
            }
        },
        async setCompleted() {
            if (this.checked.length > 0) {
                this.busy_completing = true;
                const ids = this.checked.map((complaint) => complaint.id);
                await apiClient.post('/vue/suppliers/complaints/batch/complete', { complaint_ids: ids })
                    .then(async (response) => {
                        this.complaint = response.data;
                        this.$refs.btncompletecomplaint.triggerSaved();
                        this.$refs.complaintstable.reload();
                    })
                    .catch(() => {
                        this.$refs.btncompletecomplaint.triggerFailed();
                    })
                    .finally(() => {
                        this.busy_completing = false;
                    });
            } else {
                await this.$swal({
                    text: 'Inga reklamationer är valda',
                    icon: 'warning',
                    timer: 3000,
                });
            }
        },
        async sendComplaint(e) {
            e.preventDefault();
            const isValid = await this.$refs.sendcomplaintform.validate();
            if (isValid) {
                this.busy_sending = true;
                const ids = this.checked.map((complaint) => complaint.id);
                await apiClient.post('/vue/suppliers/complaints/batch/send', { complaint_ids: ids, ...this.send })
                    .then(() => {
                        this.$bvModal.hide('modal-send');
                    }).catch(() => {
                        this.$refs.btnsendpurchase.triggerFailed();
                    }).finally(() => {
                        this.busy_sending = false;
                    });
            } else {
                this.$refs.btnsendpurchase.triggerFailed();
            }
        },
        async showModalSend() {
            if (this.checked.length > 0) {
                let hasSent = false;
                this.send.from = this.user.email;
                this.send.subject = '';
                this.send.message = '';
                this.checked.forEach((complaint) => {
                    this.send.subject += (this.send.subject === '' ? 'Product Complaint ' : ', ') + ('#' + complaint.id);
                    this.send.message += 'Product Complaint: #' + complaint.id + '\n';
                    this.send.message += 'Product: #' + complaint.ProdSupplierSKU + ' ' + (complaint.InternalName ?? complaint.ProdName) + ' (SKU ' + complaint.ProdID + ')\n\n';
                    this.send.message += (complaint.description ?? '') + '\n\n';
                    if (complaint.sent_ts) {
                        hasSent = true;
                    }
                });
                let open = true;
                if (hasSent) {
                    await this.$swal({
                        text: 'En eller flera reklamationer har redan skickats, vill du skicka igen?',
                        icon: 'warning',
                        showCancelButton: true,
                        confirmButtonText: 'Ja!',
                        cancelButtonText: 'Nej',
                    }).then((result) => {
                        if (result.isConfirmed) {
                            open = true;
                        } else {
                            open = false;
                        }
                    });
                }
                if (open) {
                    this.$bvModal.show('modal-send');
                }
            } else {
                await this.$swal({
                    text: 'Inga reklamationer är valda',
                    icon: 'warning',
                    timer: 3000,
                });
            }
        },
        selectAllThisPage() {
            const currentPageRows = this.$refs.complaintstable.getData('data');
            const intersect = currentPageRows.filter((row) => this.checked.includes(row));
            if (currentPageRows.length === intersect.length) {
                this.checked = this.checked.filter((row) => !currentPageRows.includes(row));
            } else {
                this.checked = this.checked.concat(currentPageRows);
            }
        },
    },
    computed: {
        ...mapState(useAuthStore, ['user']),
        selected_is_same_supplier() {
            let find = null;
            if (this.checked[0]) {
                const supplierId = this.checked[0].supplier_id;
                find = this.checked.find((row) => row.supplier_id !== supplierId);
            }
            return !(find);
        },
    },
};
</script>
